// Polls the workflow run detail page every `intervalValue` ms while the run
// is in a non-terminal state (running or pending).
// Sends a Turbo Stream request to the `refreshUrl` and lets Turbo apply
// the returned stream actions — only the meta card and the result panel
// are replaced, not the entire page.
// After each poll it reads `data-run-status` from the meta element to decide
// whether to stop polling (completed / failed are terminal).

import { Controller } from "@hotwired/stimulus"

const TERMINAL_STATUSES = new Set(["completed", "failed"])

export default class extends Controller {
  static values = {
    refreshUrl: String,
    metaId:     String,
    interval:   { type: Number, default: 5000 },
  }

  connect() {
    if (!this._isTerminal()) {
      this._startPolling()
    }
  }

  disconnect() {
    this._stopPolling()
  }

  // ── private ──────────────────────────────────────────────────────────────

  _isTerminal() {
    const meta = document.getElementById(this.metaIdValue)
    if (!meta) return false
    return TERMINAL_STATUSES.has(meta.dataset.runStatus)
  }

  _startPolling() {
    if (this._timer) return
    this._timer = setInterval(() => this._poll(), this.intervalValue)
  }

  _stopPolling() {
    if (!this._timer) return
    clearInterval(this._timer)
    this._timer = null
  }

  async _poll() {
    if (!this.hasRefreshUrlValue) return
    try {
      const url = new URL(this.refreshUrlValue, window.location.origin)
      url.searchParams.set("_t", Date.now())
      const response = await fetch(url.toString(), {
        headers: {
          Accept:             "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })
      if (!response.ok) return
      const contentType = response.headers.get("Content-Type") || ""
      if (!contentType.includes("turbo-stream")) return

      const html = await response.text()
      Turbo.renderStreamMessage(html)

      // Stop polling once the run has reached a terminal state
      if (this._isTerminal()) {
        this._stopPolling()
      }
    } catch (_) {
      // silently ignore network errors — polling will retry next interval
    }
  }
}
